import { createSlice } from "@reduxjs/toolkit";
import { signUp as signUpThunk } from "./signUpThunk";
import { signIn as signInThunk } from "./signInThunk";
import { signInWithGoogle as signInWithGoogleThunk } from "./Google/signInWithGoogleThunk";
import { signUpWithGoogle as signUpWithGoogleThunk } from "./Google/signUpWithGoogleThunk";
import { forgetPassword as forgetPasswordThunk } from "./forgetPasswordThunk";
import { updateProfile as updateProfileThunk } from "./updateProfileThunk";


interface authState {
  user: {
    userName: string | null;
    userImage: string | null;
    userLocation: string | null;
    userEmail: string | null;
    phoneNumber: string | null;
    uid: string | null;
  };
  token: string | null;

  signUp: {
    signUpLoading: boolean;
    signUpError: string | null;
  };
  signIn: {
    signInLoading: boolean;
    signInError: string | null;
  };
  forgotPassword: {
    forgotPasswordLoading: boolean;
    forgotPasswordError: string | null;
  };
  updateProfile: {
    updateProfileLoading: boolean;
    updateProfileError: string | null;
  };
  fetchUserProfile: {
    fetchUserProfileLoading: boolean;
    fetchUserProfileError: string | null;
  };
}

const initialState: authState = {
  token: null,
  user: {
    userName: null,
    userImage: null,
    userLocation: null,
    userEmail: null,
    phoneNumber: null,
    uid: null
  },
  signUp: {
    signUpLoading: false,
    signUpError: null
  },
  signIn: {
    signInLoading: false,
    signInError: null
  },
  forgotPassword: {
    forgotPasswordLoading: false,
    forgotPasswordError: null
  },
  updateProfile: {
    updateProfileLoading: false,
    updateProfileError: null
  },
  fetchUserProfile: {
    fetchUserProfileLoading: false,
    fetchUserProfileError: null
  }
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: () => {
      return { ...initialState };
    }
  },
  extraReducers(builder) {
    builder.addCase(signUpThunk.pending, (state) => {
      state.signUp.signUpLoading = true;
      state.signUp.signUpError = null;
      return state;
    });
    builder.addCase(signUpThunk.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.user.userName = action.payload.userName;
      state.user.userEmail = action.payload.userEmail;
      state.signUp.signUpLoading = false;
      state.user.uid = action.payload.uid;
      return state;
    });
    builder.addCase(signUpThunk.rejected, (state, action) => {
      state.signUp.signUpLoading = false;
      state.signUp.signUpError =
        action?.payload?.message || "Something went wrong";
      return state;
    });
    builder.addCase(signInThunk.pending, (state) => {
      state.signIn.signInLoading = true;
      return state;
    });
    builder.addCase(signInThunk.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.user.userName = action.payload.userName;
      state.user.userEmail = action.payload.userEmail;
      state.user.uid = action.payload.uid;
      state.signIn.signInLoading = false;
      state.signIn.signInError = null;

      return state;
    });
    builder.addCase(signInThunk.rejected, (state, action) => {
      state.signIn.signInLoading = false;
      state.signIn.signInError =
        action?.payload?.message || "Something went wrong";
      return state;
    });
    builder.addCase(signInWithGoogleThunk.pending, (state) => {
      state.signIn.signInLoading = true;
      return state;
    });
    builder.addCase(signInWithGoogleThunk.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.user.userImage = action.payload.userPhoto;
      state.user.userName = action.payload.userName;
      state.user.userEmail = action.payload.userEmail;
      state.user.uid = action.payload.uid;
      state.signIn.signInLoading = false;
      state.signIn.signInError = null;
      return state;
    });
    builder.addCase(signInWithGoogleThunk.rejected, (state, action) => {
      state.signIn.signInLoading = false;
      state.signIn.signInError =
        action?.payload?.message || "Something went wrong";
      return state;
    });
    builder.addCase(signUpWithGoogleThunk.pending, (state) => {
      state.signUp.signUpLoading = true;
      return state;
    });
    builder.addCase(signUpWithGoogleThunk.fulfilled, (state, action) => {
      state.token = action.payload.token;
      state.user.userName = action.payload.userName;
      state.user.userEmail = action.payload.userEmail;
      state.user.userImage = action.payload.userPhoto;
      state.user.uid = action.payload.uid;
      state.signUp.signUpLoading = false;
      state.signUp.signUpError = null;
      return state;
    });
    builder.addCase(signUpWithGoogleThunk.rejected, (state, action) => {
      state.signUp.signUpLoading = false;
      state.signUp.signUpError =
        action?.payload?.message || "Something went wrong";
      return state;
    });
    builder.addCase(forgetPasswordThunk.pending, (state) => {
      state.forgotPassword.forgotPasswordLoading = true;
      return state;
    });
    builder.addCase(forgetPasswordThunk.fulfilled, (state) => {
      state.forgotPassword.forgotPasswordLoading = false;
      state.forgotPassword.forgotPasswordError = null;
      return state;
    });
    builder.addCase(forgetPasswordThunk.rejected, (state, action) => {
      state.forgotPassword.forgotPasswordLoading = false;
      state.forgotPassword.forgotPasswordError =
        action?.payload?.message || "Something went wrong";
      return state;
    });

    // Update Profile cases
    builder.addCase(updateProfileThunk.pending, (state) => {
      state.updateProfile.updateProfileLoading = true;
      state.updateProfile.updateProfileError = null;
      return state;
    });
    builder.addCase(updateProfileThunk.fulfilled, (state, action) => {
      state.user.userName = action.payload.userName;
      state.user.userLocation = action.payload.userLocation;
      state.user.phoneNumber = action.payload.phoneNumber;
      state.updateProfile.updateProfileLoading = false;
      return state;
    });
    builder.addCase(updateProfileThunk.rejected, (state, action) => {
      state.updateProfile.updateProfileLoading = false;
      state.updateProfile.updateProfileError =
        action?.payload?.message || "Failed to update profile";
      return state;
    });

    // Fetch User Profile cases
    // builder.addCase(fetchUserProfileThunk.pending, (state) => {
    //   state.fetchUserProfile.fetchUserProfileLoading = true;
    //   state.fetchUserProfile.fetchUserProfileError = null;
    //   return state;
    // });
    // builder.addCase(fetchUserProfileThunk.fulfilled, (state, action) => {
    //   state.user.userName = action.payload.userName;
    //   state.user.userLocation = action.payload.userLocation;
    //   state.user.phoneNumber = action.payload.phoneNumber;
    //   state.user.userEmail = action.payload.userEmail;
    //   state.user.userImage = action.payload.userImage;
    //   state.fetchUserProfile.fetchUserProfileLoading = false;
    //   return state;
    // });
    // builder.addCase(fetchUserProfileThunk.rejected, (state, action) => {
    //   state.fetchUserProfile.fetchUserProfileLoading = false;
    //   state.fetchUserProfile.fetchUserProfileError =
    //     action?.payload?.message || "Failed to fetch user profile";
    //   return state;
    // });
  }
});

export {
  signUpThunk,
  signInThunk,
  signInWithGoogleThunk,
  signUpWithGoogleThunk,
  forgetPasswordThunk,
  updateProfileThunk,
  // fetchUserProfileThunk
};

export default authSlice.reducer;
